import type { InferContractRouterInputs } from '@orpc/contract'
import type { baseContract } from 'pixai-contract/api/orpc'
import type { ReactNode } from 'react'
import { useCallback, useRef, useState } from 'react'
import type {
  CaptchaToken,
  VisibleCaptchaRef,
} from 'src/components/Account/Captcha/Captcha'
import { VisibleTurnstileWrapper } from 'src/components/Account/Captcha/TurnstileWrapper'
import type { ArtworkBaseFragment } from 'src/services/apollo/generated/graphql'
import { ArtworkVisibilityType } from 'src/services/apollo/generated/graphql'
import { contractClient } from '../orpc/contract'

export type CreateArtworkInput = InferContractRouterInputs<
  typeof baseContract
>['artwork']['createFromMedia']

async function executeCreateArtworkOrpc(
  input: CreateArtworkInput,
  turnstileToken?: string,
) {
  const headers: Record<string, string> = {}

  if (turnstileToken) {
    headers['X-Turnstile-Token'] = turnstileToken
  }

  return contractClient.artwork.createFromMedia(input, {
    context: { headers },
  })
}

/**
 * Base hook for creating artwork with optional Turnstile verification
 */
export function useCreateArtwork(
  getTurnstileToken?: () => string | null | Promise<string | null>,
) {
  const create = useCallback(
    async (input: CreateArtworkInput): Promise<ArtworkBaseFragment | null> => {
      const turnstileToken = (await getTurnstileToken?.()) ?? undefined
      const result = await executeCreateArtworkOrpc(input, turnstileToken)

      if (!result) return null

      // Convert oRPC response to Artwork type
      return {
        __typename: 'Artwork',
        id: result.id,
        mediaId: result.mediaId,
        title: result.title,
        prompts: result.prompts,
        hidePrompts: result.hidePrompts,
        visibility:
          result.visibility === 'PRIVATE'
            ? ArtworkVisibilityType.Private
            : ArtworkVisibilityType.Public,
        isSensitive: result.isSensitive,
        type: result.type,
      } as ArtworkBaseFragment
    },
    [getTurnstileToken],
  )

  return { create }
}

/**
 * Hook that combines useCreateArtwork with Turnstile verification.
 * Returns a TurnstileWidget component that should be rendered in your component.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { TurnstileWidget, turnstileReady, create } = useCreateArtworkWithTurnstile()
 *
 *   return (
 *     <>
 *       <TurnstileWidget />
 *       <button disabled={!turnstileReady} onClick={() => create(input)}>
 *         Submit
 *       </button>
 *     </>
 *   )
 * }
 * ```
 */
export function useCreateArtworkWithTurnstile() {
  const turnstileRef = useRef<VisibleCaptchaRef>(null)
  const [captchaToken, setCaptchaToken] = useState<CaptchaToken>()

  const getTurnstileToken = useCallback(() => {
    return captchaToken?.token ?? null
  }, [captchaToken])

  const { create: baseCreate } = useCreateArtwork(getTurnstileToken)

  const create = useCallback(
    async (input: CreateArtworkInput) => {
      const result = await baseCreate(input)
      // Reset turnstile after submission
      turnstileRef.current?.reset?.()
      setCaptchaToken(undefined)
      return result
    },
    [baseCreate],
  )

  const TurnstileWidget = useCallback(
    (): ReactNode => (
      <VisibleTurnstileWrapper
        ref={turnstileRef}
        setToken={setCaptchaToken}
        action="artworkUpload"
      />
    ),
    [],
  )

  const turnstileReady = Boolean(captchaToken?.token)

  return {
    create,
    TurnstileWidget,
    turnstileReady,
  }
}
